import { z } from "zod";

export enum IdentityRole {
  Owner = "owner",
  Executive = "executive",
  Marketing = "marketing",
  Sales = "sales",
  Creator = "creator",
  Advisor = "advisor",
  Guest = "guest",
  Bot = "bot",
}

export enum OnboardingCalloutKey {
  Assistant = "assistant",
  Dashboard = "dashboard",
  Build = "build",
  Create = "create",
  Ideas = "ideas",
  DocumentUpload = "documentUpload",
  AssistantTasks = "assistantTasks",
  SmartBlocks = "smartBlocks",
  Stacks = "stacks",
  FirstBlockOpen = "firstBlockOpen",
  FirstCreate = "firstCreate",
}

export enum ContactLayout {
  Grid = "grid",
  List = "list",
}

export const IdentityRoleSchema = z.enum(IdentityRole);

/**
 * User settings for the NOAN assistant
 */
export const AssistantSettingsSchema = z.object({
  useMemories: z.boolean().optional().default(true),
});

export const IdentitySettingsSchema = z.object({
  /** @deprecated */
  onboardingCalloutProgress: z.enum(OnboardingCalloutKey).optional(),

  /** @deprecated */
  onboardingCalloutOneOff: z
    .array(z.enum(OnboardingCalloutKey))
    .default([])
    .optional(),

  contact: z
    .object({
      layout: z.enum(ContactLayout).optional(),
    })
    .optional(),

  assistant: AssistantSettingsSchema.optional(),

  whatsapp: z
    .object({
      phoneNumber: z.string().optional(),
      verifyCode: z.string().nullable().optional(),
      verifyCodeSentAt: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

export const IdentitySchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  email: z.string(),
  primaryRole: IdentityRoleSchema,
  organizationId: z.uuid(),
  authUserId: z.string().nullable().optional(),
  settings: IdentitySettingsSchema,
  createdAt: z.date(),
});

/**
 * Minimal subset of Identity used for e.g including creator data as part of
 * another record (fact->creator->...)
 */
export const IdentityEmbedSchema = z.object({
  id: IdentitySchema.shape.id,
  name: IdentitySchema.shape.name.nullable(),
  email: IdentitySchema.shape.email.nullable(),
});

export const IdentityWithAccountIdSchema = z.object({
  ...IdentitySchema.shape,
  accountId: z.uuid(),
});

const PlanUsageItemSchema = z.object({
  used: z.number(),
  limit: z.number(),
});

/** A null item is unlimited on the plan. */
export const PlanUsageSchema = z.object({
  facts: PlanUsageItemSchema.nullable(),
  contacts: PlanUsageItemSchema.nullable(),
  apiKeys: PlanUsageItemSchema.nullable(),
  agentKeys: PlanUsageItemSchema.nullable(),
  organizations: PlanUsageItemSchema.nullable(),
  seats: PlanUsageItemSchema.nullable(),
});

export const IdentitySummarySchema = z.object({
  isAccountActive: z.boolean(),
  isPayingCustomer: z.boolean(),
  isCustomer: z.boolean(),
  hasInsightsAccess: z.boolean(),
  identity: IdentityWithAccountIdSchema.nullable(),
  mustCompleteOnboarding: z.boolean(),
  hasDubId: z.boolean(),
});

export const identityNameMaxLength = 64;

/** HTML and template brackets, backslash, control characters and bidi overrides */
const forbiddenNameCharacters = /[<>{}\\\p{Cc}\u202A-\u202E\u2066-\u2069]/u;

export const IdentityNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(identityNameMaxLength)
  .refine((name) => !forbiddenNameCharacters.test(name), {
    message: "Name can't contain < > { } \\ or control characters",
  });

export const UpdateProfileSchema = z.object({
  settings: IdentitySchema.shape.settings.optional(),
  name: IdentityNameSchema.optional(),
});

export const UpdateMemberSchema = z.object({
  id: z.string(),
  name: IdentityNameSchema.optional(),
  role: IdentitySchema.shape.primaryRole.optional(),
});

export type IdentitySummary = z.infer<typeof IdentitySummarySchema>;
export type PlanUsageItem = z.infer<typeof PlanUsageItemSchema>;
export type PlanUsage = z.infer<typeof PlanUsageSchema>;
export type Identity = z.infer<typeof IdentitySchema>;
export type IdentityWithAccountId = z.infer<typeof IdentityWithAccountIdSchema>;
export type IdentitySettings = z.infer<typeof IdentitySettingsSchema>;
export type UpdateProfile = z.infer<typeof UpdateProfileSchema>;
export type UpdateMember = z.infer<typeof UpdateMemberSchema>;
